/**
 * The transcript block of the call pane: the finished transcript, the live
 * preview that stands in for it, or the reason there is neither.
 *
 * The preview is the part worth explaining. The after-call transcript is made
 * from the recording, and that recording has to finish at Twilio, be downloaded
 * here and then be read by a model - about a minute, sometimes two. For that
 * whole minute the pane used to say "the transcript is being prepared" and show
 * nothing, which is not only unhelpful but untrue: the co-pilot wrote the same
 * conversation down while it was happening, fifteen seconds at a time, and those
 * lines are already on the row. So they are shown, clearly labelled as the
 * preliminary version, and the moment the real transcript exists the server
 * stops sending them and this switches over on its own.
 *
 * Two transcripts of one call are never on screen together. Which one is
 * authoritative is not a question a reader should have to answer.
 */
import { FileText, Link2, Pencil, RefreshCw } from "lucide-react";
import { cn } from "@wa/ui";
import { CallSpend } from "./cost";
import { useT } from "../../../i18n/I18nProvider";
import type { CallDetail } from "../../../lib/calls";
import { Avatar, IconBtn } from "../../ui";
import { CallTextEdit } from "./CallTextEdit";
import { CallTimedTurns } from "./CallTimedTurns";
import { keepsSpeakers, parseTranscript, readTimedTurns, speakerNames } from "./transcript";
import { transcriptErrorIsPermanent, transcriptErrorKey } from "./transcriptError";

export function CallTranscript({
  call,
  retrying,
  onRetry,
  editing,
  onEditing,
  onSaveText,
  onSeek,
}: {
  call: CallDetail;
  retrying: boolean;
  onRetry: () => void;
  editing: boolean;
  onEditing: (v: boolean) => void;
  /** Absent when this viewer may see the call but not hear it. */
  onSaveText?: (value: string | null) => Promise<void>;
  /** Steer the recording above. Absent when this call has no stored audio. */
  onSeek?: (seconds: number) => void;
}) {
  const t = useT();
  const done = call.transcriptStatus === "done";
  const none = call.transcriptStatus === "none";
  /**
   * What an empty block says. "The call has no recording" is only true when it
   * has none: a row can hold audio and no transcript (switched off for this
   * call, or an AI call from before its tape was queued), and with the player
   * drawn right above, that sentence reads as a fault in the page.
   */
  const emptyKey = !none
    ? "calls.transcript.waiting"
    : call.recordingStatus === "stored"
      ? "calls.transcript.noneKept"
      : "calls.transcript.noneDetail";
  const whyFailed = transcriptErrorKey(call.transcriptError);
  const parsed = done && call.transcriptText ? parseTranscript(call.transcriptText) : null;
  /**
   * The timed reading of the same conversation, when there is one.
   *
   * Preferred over `parsed` wherever it answers, because it is better on both
   * counts that matter: the speakers are the transcriber's own labels rather
   * than prefixes found in prose, and each line knows where it sits in the
   * audio. It is null on most rows and that is ordinary - see `readTimedTurns`.
   *
   * ⚠ THE TWO ARE NEVER ON SCREEN TOGETHER, the same rule the live preview
   * follows. They are one call read twice, and which one is authoritative is not
   * a question a reader should have to answer.
   *
   * ⚠ An operator's correction takes this away, on purpose. The server clears
   * the column when the verbatim half is edited, so the pane drops back to the
   * text - which is now the operator's own. Timings measured against the words
   * the model heard would otherwise be drawn beside words nobody measured.
   */
  const timed = done ? readTimedTurns(call.transcriptTurns) : null;
  // Tone follows the speaker, not the row: alternating colours down a list of
  // turns that all belong to one person reads as a conversation that is not there.
  const speakers = parsed ? speakerNames(parsed.turns) : [];
  const live = call.liveTurns ?? [];

  return (
    <>
      <div className="crm-tr-t">
        <FileText className="size-3.5" />
        {t("calls.detail.transcript")}
        {/* Also on a row with audio and no transcript at all: the route behind
            it re-arms any stored recording, so the block never ends on a
            sentence with nothing to do about it. */}
        {(call.transcriptStatus === "failed" || done || none) && call.recordingStatus === "stored" ? (
          <IconBtn
            small
            onClick={onRetry}
            disabled={retrying}
            title={t(none ? "calls.detail.transcribeNow" : "calls.detail.retry")}
          >
            <RefreshCw className={cn("size-3.5", retrying && "animate-spin")} />
          </IconBtn>
        ) : null}
        {onSaveText && done && !editing ? (
          <IconBtn small onClick={() => onEditing(true)} title={t("calls.detail.editTranscript")}>
            <Pencil className="size-3.5" />
          </IconBtn>
        ) : null}
      </div>

      {editing && onSaveText ? (
        <CallTextEdit
          value={call.transcriptText}
          editing={editing}
          onEditing={onEditing}
          onSave={onSaveText}
          rows={14}
          // Said BEFORE the save, while it is still one keystroke to undo: the
          // parser needs two distinct "Name:" prefixes and silently drops every
          // label below that, which reads as a broken split rather than an edit.
          warn={(draft) =>
            draft.trim() && !keepsSpeakers(draft) ? t("calls.transcript.editLosesSpeakers") : null
          }
        />
      ) : null}

      {timed || parsed ? (
        <>
          {timed ? (
            <CallTimedTurns turns={timed} onSeek={onSeek} />
          ) : parsed ? (
            <>
              {parsed.turns.length > 0 && !parsed.attributed ? (
                <p className="mb-3 text-[11px] text-muted-foreground">
                  {t("calls.transcript.unattributed")}
                </p>
              ) : null}
              {parsed.turns.map((turn, i) => (
                <div key={i} className="crm-turn">
                  {turn.who ? (
                    <Avatar name={turn.who} size={30} tone={speakers.indexOf(turn.who) % 2 ? "brand" : "soft"} />
                  ) : (
                    <Link2 className="mt-1 size-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="min-w-0 flex-1">
                    {turn.who ? <span className="crm-turn-n">{turn.who}</span> : null}
                    <span className="crm-turn-p block">{turn.line}</span>
                  </span>
                </div>
              ))}
            </>
          ) : null}
          {call.transcriptModel ? (
            <p className="mt-4 text-[11px] text-muted-foreground">
              {t("calls.detail.model", { model: call.transcriptModel })}
            </p>
          ) : null}
          <CallSpend call={call} />
        </>
      ) : (
        <>
          {/* The status line comes first even when a preview follows it. A
              failure that the reader only discovers under three screens of
              preliminary text is a failure nobody retries. */}
          {call.transcriptStatus === "failed" ? (
            <p className={cn("text-sm text-destructive", live.length > 0 && "mb-3")}>
              {/* A sentence when the code is one of ours, the raw text when it
                  is the provider's - see `transcriptError.ts` on why an unknown
                  code is never dressed up. */}
              {whyFailed
                ? t(whyFailed)
                : t("calls.transcript.failedDetail", { error: call.transcriptError ?? "" })}
              {transcriptErrorIsPermanent(call.transcriptError) ? (
                <span className="mt-1 block text-[11px] text-muted-foreground">
                  {t("calls.transcript.why.permanent")}
                </span>
              ) : null}
            </p>
          ) : live.length > 0 ? (
            <p className="mb-3 text-[11px] text-muted-foreground">{t("calls.transcript.livePreview")}</p>
          ) : (
            <p className="text-sm text-muted-foreground">{t(emptyKey)}</p>
          )}

          {live.map((turn, i) => {
            // ⚠ WHETHER THE LABEL IS EVIDENCE DEPENDS ON WHICH LISTENER RAN, and
            // the comment that used to sit here ("the two sides arrive on
            // separate channels, so the label is an agreement rather than an
            // inference") was true of only one of them. It holds for the Scribe
            // listener, which is handed two mono clips. Gemini is handed one
            // downmixed clip and infers the speaker from what a sentence sounds
            // like, so it may honestly answer "unknown" - and an unattributed
            // line is drawn the way the finished transcript draws one, with a
            // link icon and no name, rather than being forced into a side.
            const unknown = turn.speaker === "unknown";
            const who = t(
              turn.speaker === "operator"
                ? "calls.transcript.speakerOperator"
                : "calls.transcript.speakerClient",
            );
            return (
              <div key={`${turn.seq}-${i}`} className="crm-turn">
                {unknown ? (
                  <Link2 className="mt-1 size-4 shrink-0 text-muted-foreground" />
                ) : (
                  <Avatar name={who} size={30} tone={turn.speaker === "operator" ? "soft" : "brand"} />
                )}
                <span className="min-w-0 flex-1">
                  {unknown ? null : <span className="crm-turn-n">{who}</span>}
                  <span className="crm-turn-p block">{turn.text}</span>
                </span>
              </div>
            );
          })}
        </>
      )}
    </>
  );
}
